import * as React from 'react'
import PropTypes from 'prop-types'

// --- Lib ---
import { parseShortcutKeys } from '@tiptap-ui/lib/tiptap-utils'

// --- Hooks ---
import { useTiptapEditor } from '@tiptap-ui/hooks/use-tiptap-editor'

import { MARK_SHORTCUT_KEYS, useMark } from '@tiptap-ui/components/tiptap-ui/mark-button'

import { Button } from '@tiptap-ui/components/tiptap-ui-primitive/button'
import { Badge } from '@tiptap-ui/components/tiptap-ui-primitive/badge'

export function MarkShortcutBadge ({
  type,
  attrs,
  shortcutKeys = MARK_SHORTCUT_KEYS[attrs?.type ? `${type}_${attrs.type}` : type],
}) {
  return <Badge>{parseShortcutKeys({ shortcutKeys })}</Badge>
}

MarkShortcutBadge.propTypes = {
  type: PropTypes.string.isRequired,
  attrs: PropTypes.object,
  shortcutKeys: PropTypes.string,
}

/**
 * Button component for toggling marks in a Tiptap editor.
 *
 * For custom button implementations, use the `useMark` hook instead.
 */
export const MarkButton = React.forwardRef((
  {
    editor: providedEditor,
    type,
    attrs,
    text,
    tooltip,
    hideWhenUnavailable = false,
    onToggled,
    showShortcut = false,
    onClick,
    children,
    ...buttonProps
  },
  ref
) => {
  const { editor } = useTiptapEditor(providedEditor)
  const {
    isVisible,
    handleMark,
    label,
    canToggle,
    isActive,
    Icon,
    shortcutKeys,
  } = useMark({
    editor,
    type,
    attrs,
    hideWhenUnavailable,
    onToggled,
  })

  const handleClick = React.useCallback((event) => {
    onClick?.(event)
    if (event.defaultPrevented) return
    handleMark()
  }, [handleMark, onClick])

  if (!isVisible) {
    return null
  }

  return (
    <Button
      type='button'
      disabled={!canToggle}
      data-style='ghost'
      data-active-state={isActive ? 'on' : 'off'}
      data-disabled={!canToggle}
      role='button'
      tabIndex={-1}
      aria-label={label}
      aria-pressed={isActive}
      tooltip={tooltip ?? label}
      onClick={handleClick}
      {...buttonProps}
      ref={ref}
    >
      {children ?? (
        <>
          <Icon className='tiptap-button-icon' />
          {text && <span className='tiptap-button-text'>{text}</span>}
          {showShortcut && (
            <MarkShortcutBadge type={type} attrs={attrs} shortcutKeys={shortcutKeys} />
          )}
        </>
      )}
    </Button>
  )
})

MarkButton.displayName = 'MarkButton'
MarkButton.propTypes = {
  editor: PropTypes.object,
  type: PropTypes.string.isRequired,
  attrs: PropTypes.object,
  text: PropTypes.string,
  tooltip: PropTypes.node,
  hideWhenUnavailable: PropTypes.bool,
  onToggled: PropTypes.func,
  showShortcut: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
}
